import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { SEO } from '@/components/common/SEO';
import { buildOrganizationSchema, buildWebSiteSchema } from '@/lib/schema';

import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';

import TrustHero from '@/components/landing/professional/TrustHero';
import TrustLogos from '@/components/landing/professional/TrustLogos';
import VideoDemoSection from '@/components/landing/professional/VideoDemoSection';
import BlackBoxProblem from '@/components/landing/professional/BlackBoxProblem';
import CapabilitiesSection from '@/components/landing/professional/CapabilitiesSection';
import AntiSurveillanceStrip from '@/components/landing/professional/AntiSurveillanceStrip';
import WhyDifferent from '@/components/landing/professional/WhyDifferent';
import TestimonialQuote from '@/components/landing/professional/TestimonialQuote';
import HowItWorks from '@/components/landing/HowItWorks';
import SecurityFirst from '@/components/landing/professional/SecurityFirst';
import FAQSection from '@/components/landing/FAQSection';
import ProfessionalCTA from '@/components/landing/professional/ProfessionalCTA';
import StickyCtaBar from '@/components/landing/professional/StickyCtaBar';
import ExitIntentPopup from '@/components/landing/professional/ExitIntentPopup';

export default function ProfessionalLandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null;
  }

  const appDescription =
    'Engineering analytics platform that tracks cycle times, DORA metrics, and team velocity from GitHub data.';

  // Home page carries the standalone Organization + WebSite brand entities
  // alongside the product's SoftwareApplication schema.
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'CodePulse',
      description: appDescription,
      url: 'https://codepulsehq.com',
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free tier available',
      },
      featureList: [
        'DORA Metrics Tracking',
        'Cycle Time Analysis',
        'Team Velocity Metrics',
        'Code Review Analytics',
        'Knowledge Silo Detection',
        'AI Tools ROI Measurement',
        'Investment Allocation',
        'Developer Experience Surveys',
        'Industry Benchmarks',
        'Jira, Linear & Azure DevOps Integrations',
        'GitHub Integration',
      ],
    },
    buildOrganizationSchema(),
    buildWebSiteSchema(appDescription),
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="CodePulse - Engineering Analytics for High-Performing Teams"
        description="Understand where engineering time goes with read-only GitHub analysis. Get your Engineering Health Report in 15 minutes. We never read your source files."
        structuredData={structuredData}
      />
      <LandingHeader />
      <main>
        {/* 1. Hero with primary CTA */}
        <TrustHero />

        {/* 2. Trust indicators - credibility stats */}
        <TrustLogos />

        {/* 3. Black Box Problem - establish the pain first */}
        <BlackBoxProblem />

        {/* 4. Video Demo - now the product is the answer to the problem */}
        <VideoDemoSection />

        {/* 5. What you get - capabilities with screenshots + proof */}
        <CapabilitiesSection />

        {/* 5b. Trust - team-first, anti-surveillance (answers the objection seeing all those metrics raises) */}
        <AntiSurveillanceStrip />

        {/* 6. Customer testimonials - social proof validates capabilities */}
        <TestimonialQuote />

        {/* 7. Why this is different - competitive differentiation */}
        <WhyDifferent />

        {/* 8. How It Works - answer "how hard?" */}
        <HowItWorks />

        {/* 9. Security & Anti-Surveillance */}
        <SecurityFirst />

        {/* 7. FAQ - pre-expand top question */}
        <FAQSection />

        {/* 8. Final CTA - matches hero */}
        <ProfessionalCTA />
      </main>
      <LandingFooter />

      {/* Sticky CTA Bar - appears on scroll */}
      <StickyCtaBar />

      {/* Exit Intent Popup - captures leaving visitors */}
      <ExitIntentPopup />
    </div>
  );
}
